import React, { useMemo, useState } from "react";
import { Mail } from "lucide-react";
import { TOTAL_TICKETS, TICKET_PRICE, formatUSD } from "../utils/formatters";
import { STATUS } from "../constants/colors";
import { TenderForm } from "./TenderForm";
import { PaymentReminderModal } from "./PaymentReminderModal";

export const DepositTab = ({ tickets, currentUser, onBulkMarkFunds, drawings, fetchReminderEligible, sendPaymentReminders, showToast }) => {
  const isAdmin = currentUser?.role === "admin";
  const [selected, setSelected] = useState({}); // {n: true}
  const [includeReserved, setIncludeReserved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [tenderModalOpen, setTenderModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);

  const pendingTickets = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      if (t.status === "sold" || (includeReserved && t.status === "reserved")) {
        arr.push({ n: i, t });
      }
    }
    return arr;
  }, [tickets, includeReserved]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pendingTickets;
    return pendingTickets.filter(({ n, t }) =>
      String(n).includes(q) ||
      (t.buyer || "").toLowerCase().includes(q) ||
      (t.assigned_seller_name || "").toLowerCase().includes(q)
    );
  }, [pendingTickets, search]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]).map(Number),
    [selected]
  );
  const selectedAmount = selectedIds.length * TICKET_PRICE;

  const toggle = (n) => setSelected((s) => ({ ...s, [n]: !s[n] }));
  const toggleAll = () => {
    if (filtered.every(({ n }) => selected[n])) {
      const next = { ...selected };
      filtered.forEach(({ n }) => { delete next[n]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach(({ n }) => { next[n] = true; });
      setSelected(next);
    }
  };

  const submit = () => {
    setError("");
    if (selectedIds.length === 0) { setError("Select at least one ticket."); return; }
    setTenderModalOpen(true);
  };

  const confirmTender = async (tender) => {
    setBusy(true);
    const res = await onBulkMarkFunds(selectedIds, tender);
    setBusy(false);
    setTenderModalOpen(false);
    if (!res.ok) { setError(res.error || "Could not record funds."); return; }
    const skippedCount = (res.skipped || []).length;
    const label = tender.tender_type === "check" ? `Check #${tender.check_number}` : "Cash";
    showToast?.(`${res.updated} ticket${res.updated === 1 ? "" : "s"} marked as Funds Received · ${label}` + (skippedCount > 0 ? ` · ${skippedCount} skipped` : ""));
    setSelected({});
  };

  if (!isAdmin) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-[color:var(--surface-card)] border rounded-lg p-5 text-sm" style={{ borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}>
          The Deposit screen is available to administrators only.
        </div>
      </section>
    );
  }

  const allFilteredChecked = filtered.length > 0 && filtered.every(({ n }) => selected[n]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }} data-testid="deposit-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>Record a deposit</h2>
            <p className="text-sm mt-1" style={{ color: "var(--ink-tertiary)" }}>
              Check off every sold ticket whose payment is in this deposit batch, then submit. All selected tickets move to <strong>Funds Received</strong> and a log entry is recorded for each.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setReminderModalOpen(true)}
                data-testid="deposit-send-reminders-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
                style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
                title="Email every sold-but-not-paid buyer a friendly reminder"
              >
                <Mail className="h-3.5 w-3.5" />
                Send payment reminders
              </button>
            )}
            <label className="text-xs flex items-center gap-2 px-3 py-1.5 rounded-md border bg-[color:var(--surface-card)]" style={{ borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}>
              <input
                type="checkbox"
                checked={includeReserved}
                onChange={(e) => setIncludeReserved(e.target.checked)}
                data-testid="deposit-include-reserved"
              />
              Include reserved (walk-in cash)
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Pending payment</p>
            <p className="text-xl font-semibold" style={{ color: "var(--status-sold-bg)" }}>{pendingTickets.length}</p>
          </div>
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Selected</p>
            <p className="text-xl font-semibold" style={{ color: "var(--brand-primary)" }} data-testid="deposit-selected-count">{selectedIds.length}</p>
          </div>
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Deposit total</p>
            <p className="text-xl font-semibold" style={{ color: STATUS.funds_received.bg }} data-testid="deposit-amount">{formatUSD(selectedAmount)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ticket #, buyer, or seller…"
            data-testid="deposit-search"
            className="flex-1 min-w-[200px] rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
            style={{ borderColor: "var(--border-default)" }}
          />
          <button
            type="button"
            onClick={toggleAll}
            disabled={filtered.length === 0}
            data-testid="deposit-select-all"
            className="px-4 py-2 text-xs font-medium rounded-md border bg-[color:var(--surface-card)] transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)] disabled:opacity-50"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}
          >
            {allFilteredChecked ? "Unselect all visible" : "Select all visible"}
          </button>
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: "var(--ink-tertiary)" }} data-testid="deposit-empty">
            {pendingTickets.length === 0
              ? "No tickets are currently awaiting payment. Good work!"
              : "No tickets match your search."}
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border-default)" }}>
            <div className="max-h-[460px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10" style={{ background: "var(--surface-page)" }}>
                  <tr style={{ color: "var(--ink-tertiary)" }}>
                    <th className="px-3 py-2 w-10"></th>
                    <th className="text-left px-3 py-2 font-medium">Ticket</th>
                    <th className="text-left px-3 py-2 font-medium">Buyer</th>
                    <th className="text-left px-3 py-2 font-medium">Phone</th>
                    <th className="text-left px-3 py-2 font-medium">Seller</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(({ n, t }) => {
                    const meta = STATUS[t.status] || STATUS.available;
                    const checked = !!selected[n];
                    return (
                      <tr
                        key={n}
                        className="border-t cursor-pointer hover:bg-[color:var(--surface-page)]"
                        style={{ borderColor: "var(--border-default)", background: checked ? "var(--surface-subtle)" : "transparent" }}
                        onClick={() => toggle(n)}
                        data-testid={`deposit-row-${n}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(n)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`deposit-check-${n}`}
                            aria-label={`Select ticket ${n}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-semibold">#{n}</td>
                        <td className="px-3 py-2">{t.buyer || "—"}</td>
                        <td className="px-3 py-2" style={{ color: "var(--ink-tertiary)" }}>{t.phone || "—"}</td>
                        <td className="px-3 py-2" style={{ color: "var(--ink-tertiary)" }}>{t.assigned_seller_name || "—"}</td>
                        <td className="px-3 py-2">
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: meta.bg, color: meta.text, boxShadow: `inset 0 0 0 1px ${meta.outline}` }}
                          >
                            {meta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <p className="mt-3 text-sm font-medium" style={{ color: "var(--semantic-danger)" }} data-testid="deposit-error">{error}</p>}

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => setSelected({})}
            disabled={selectedIds.length === 0}
            data-testid="deposit-clear"
            className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] disabled:opacity-50"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}
          >
            Clear selection
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || selectedIds.length === 0}
            data-testid="deposit-submit"
            className="px-5 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ background: STATUS.funds_received.bg }}
          >
            {busy ? "Recording…" : selectedIds.length === 0 ? "Mark as Funds Received" : `Mark ${selectedIds.length} as Funds Received`}
          </button>
        </div>
      </div>

      {tenderModalOpen && (
        <div
          data-testid="bulk-tender-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(11,18,20,0.55)" }}
          onClick={() => !busy && setTenderModalOpen(false)}
        >
          <div className="bg-[color:var(--surface-card)] rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[color:var(--ink-primary)]">Record deposit · {selectedIds.length} ticket{selectedIds.length === 1 ? "" : "s"}</h3>
            <p className="text-xs text-[color:var(--ink-tertiary)] mt-1 mb-4">
              The same payment type, amount and check # (if check) will be applied to each selected ticket.
              Default amount is <span className="font-semibold">${TICKET_PRICE}</span> per ticket — change it if the buyer paid more or less.
            </p>
            <TenderForm
              defaultAmount={TICKET_PRICE}
              onSubmit={confirmTender}
              onCancel={() => setTenderModalOpen(false)}
              busy={busy}
              applyLabel={`Mark ${selectedIds.length} as Funds Received`}
            />
          </div>
        </div>
      )}
      {reminderModalOpen && (
        <PaymentReminderModal
          drawings={drawings || []}
          fetchEligible={fetchReminderEligible}
          onSend={sendPaymentReminders}
          onClose={() => setReminderModalOpen(false)}
          showToast={showToast}
        />
      )}
    </section>
  );
};
